/**
 * WIREDESK Portal Kit: cliente HTTP da API administrativa (`/config/portal`).
 *
 * O contrato (formato de cada resposta e a lista de rotas) mora em
 * `types/portal.ts`. Aqui só ficam os verbos, o `If-Match` e a leitura dos
 * erros que a TELA trata sozinha (412, 422, 403, 404).
 */
import { ApiError, api, apiFetch, apiFetchRaw, type OpcoesApi } from '@/services/api';
import type {
  Conferencia,
  CorpoSecao,
  ErroDeCampo,
  ManifestoResolvido,
  Municipio,
  PortalSiteDetalhe,
  PortalSiteResumo,
  SecaoId,
  SecaoPortal,
  TokenGerado,
} from '@/types/portal';

export const BASE_PORTAL = '/config/portal';

/** Estes status são ESTADO da tela, não incidente: sem alarme vermelho global. */
const TRATADOS_PELA_TELA = { esperados: [403, 404, 412] } as const satisfies OpcoesApi;

/** `If-Match` leva a versão entre aspas, como um ETag fraco de verdade. */
export function cabecalhoIfMatch(versao: number): string {
  return `"${versao}"`;
}

export type TipoDeFalha =
  | 'conflito' // 412: outra pessoa gravou antes
  | 'validacao' // 422: o servidor recusou o conteúdo
  | 'sem_plano' // 403: a feature portal_kit não está no plano
  | 'sem_api' // 404: a API do Portal Kit não está instalada nesta instância
  | 'outra';

export function classificarFalha(erro: unknown): TipoDeFalha {
  if (!(erro instanceof ApiError)) return 'outra';
  if (erro.status === 412) return 'conflito';
  if (erro.status === 422) return 'validacao';
  if (erro.status === 403) return 'sem_plano';
  if (erro.status === 404) return 'sem_api';
  return 'outra';
}

/**
 * Os campos que o servidor recusou. Aceita o formato do FastAPI
 * (`detail: [{ loc, msg }]`), o texto simples (`detail: "..."`) e o formato
 * próprio (`detail: { erros: [{ campo, mensagem }] }`).
 */
export function errosDeCampo(erro: unknown): ErroDeCampo[] {
  if (!(erro instanceof ApiError)) return [];
  const detalhe = (erro.body as { detail?: unknown } | null)?.detail;
  if (typeof detalhe === 'string') return [{ campo: '', mensagem: detalhe }];
  if (Array.isArray(detalhe)) {
    return detalhe.map((d) => {
      const item = d as { loc?: unknown[]; msg?: unknown; campo?: unknown; mensagem?: unknown };
      if (typeof item.campo === 'string') {
        return { campo: item.campo, mensagem: String(item.mensagem ?? '') };
      }
      const caminho = (item.loc ?? []).filter((p) => p !== 'body').join('.');
      return { campo: caminho, mensagem: String(item.msg ?? 'valor inválido') };
    });
  }
  const proprios = (detalhe as { erros?: unknown } | undefined)?.erros;
  if (Array.isArray(proprios)) {
    return proprios.map((e) => ({
      campo: String((e as ErroDeCampo).campo ?? ''),
      mensagem: String((e as ErroDeCampo).mensagem ?? ''),
    }));
  }
  return [];
}

function caminhoDaSecao(siteId: string, chave: SecaoId): string {
  return `${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/colunas/${chave}`;
}

export const portalApi = {
  sites: () => api.get<PortalSiteResumo[]>(`${BASE_PORTAL}/sites`, TRATADOS_PELA_TELA),

  site: (siteId: string) =>
    api.get<PortalSiteDetalhe>(
      `${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}`,
      TRATADOS_PELA_TELA,
    ),

  /** Grava uma seção. 412 se a versão mudou; 422 se o conteúdo é inválido. */
  salvarSecao: (siteId: string, chave: SecaoId, corpo: CorpoSecao, versao: number) =>
    apiFetch<SecaoPortal>(
      caminhoDaSecao(siteId, chave),
      {
        method: 'PUT',
        headers: { 'If-Match': cabecalhoIfMatch(versao) },
        body: JSON.stringify(corpo),
      },
      TRATADOS_PELA_TELA,
    ),

  /** Remove a configuração do site: a seção volta ao que o tenant ou o produto definem. */
  restaurarSecao: (siteId: string, chave: SecaoId, versao: number) =>
    apiFetch<SecaoPortal>(
      caminhoDaSecao(siteId, chave),
      { method: 'DELETE', headers: { 'If-Match': cabecalhoIfMatch(versao) } },
      TRATADOS_PELA_TELA,
    ),

  manifesto: (siteId: string) =>
    api.get<ManifestoResolvido>(
      `${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/manifesto`,
      TRATADOS_PELA_TELA,
    ),

  conferencia: (siteId: string) =>
    api.get<Conferencia>(
      `${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/conferencia`,
      TRATADOS_PELA_TELA,
    ),

  gerarToken: (siteId: string) =>
    api.post<TokenGerado>(`${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/token`),

  revogarToken: (siteId: string) =>
    api.delete<undefined>(`${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/token`),

  atualizacaoAutomatica: (siteId: string, ligada: boolean) =>
    api.patch<PortalSiteDetalhe>(`${BASE_PORTAL}/sites/${encodeURIComponent(siteId)}/plugin`, {
      atualizacao_automatica: ligada,
    }),

  /** Busca no catálogo IBGE (5.570 municípios). Funciona sem rede externa. */
  municipios: (q: string, limite = 15) => {
    const qs = new URLSearchParams({ q, limite: String(limite) });
    return api.get<Municipio[]>(`${BASE_PORTAL}/municipios?${qs}`);
  },

  /** O zip assinado do plugin, como Blob (o download leva o Bearer, então não é um <a href>). */
  pacote: async (): Promise<{ blob: Blob; nome: string }> => {
    const r = await apiFetchRaw(`${BASE_PORTAL}/plugin/pacote`);
    const disposicao = r.headers.get('Content-Disposition') ?? '';
    const nome = /filename="?([^";]+)"?/i.exec(disposicao)?.[1] ?? 'wiredesk-portal.zip';
    return { blob: await r.blob(), nome };
  },
};

/** Entrega um Blob ao navegador como download. */
export function baixarBlob(blob: Blob, nome: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Dá tempo ao navegador de iniciar o download antes de soltar a URL.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
